import { useCallback, useEffect, useState } from "react"
import { useClient } from "urql"

import type { ProductSelectionItem, PurchaseOrderFormData } from "../Supplier/types/types"

import { usePurchaseOrderUpsert } from "./usePurchaseOrderUpsert"

import type { Product } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { validatePurchaseOrderCreation } from "@/screens/Procurement/PurchaseOrder/utils/validatePurchaseOrderCreation"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UsePurchaseOrderDialogReturn {
  open: boolean
  fetching: boolean
  handleOpen: () => void
  handleClose: () => void
  handleSubmit: (data: PurchaseOrderFormData, selectedProducts: ProductSelectionItem[]) => void
}

export const usePurchaseOrderDialog = (products: Product[] | null): UsePurchaseOrderDialogReturn => {
  const [open, setOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { handleCreatePO, createdPO, fetching } = usePurchaseOrderUpsert()
  const notify = useNotificationsStore(({ enqueueNotification }) => enqueueNotification)
  const client = useClient()

  const handleOpen = useCallback(async () => {
    const companyId = products?.[0]?.company?.companyId

    if (!companyId) {
      notify({
        type: NotificationType.Warning,
        content: "Company information is missing",
      })
      return
    }

    const validation = await validatePurchaseOrderCreation({
      products,
      companyId,
      client,
    })

    if (!validation.isValid) {
      notify({
        type: NotificationType.Warning,
        content: validation.message!,
      })
      return
    }

    setOpen(true)
  }, [products, client, notify])

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [])
  const handleSubmit = (data: PurchaseOrderFormData, selectedProducts: ProductSelectionItem[]) => {
    setIsSubmitting(true)
    handleCreatePO(data, selectedProducts)
  }

  useEffect(() => {
    if (!fetching && isSubmitting && createdPO) {
      setIsSubmitting(false)
      handleClose()
    }
  }, [fetching, isSubmitting, handleClose, createdPO])

  return { open, fetching, handleOpen, handleClose, handleSubmit }
}
